import { useEffect, useRef, useState } from "react";
import { logSession } from "utils/sentryLogger";

const HEARTBEAT_INTERVAL_MS = 60000; // 60 seconds

/**
 * Custom hook to log heartbeat events while user is in a waiting room.
 * Logs every 60 seconds with session context including wait time and user state.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.roomName - Name of the room for logging (e.g., "Ready room", "Waiting room")
 * @param {string} params.sessionId - Current session ID
 * @param {string} params.groupCode - Current group code
 * @param {string} params.userId - Current user ID
 * @param {boolean} params.hasClickedBegin - Whether user has clicked "Let's Begin"
 * @param {number} params.participantCount - Number of participants in the room
 * @param {string} params.role - User role in session
 * @param {string} params.authMethod - Authentication method used
 * @returns {Object} - Hook state including totalWaitTime
 */
export const useReadyRoomHeartbeat = ({
  roomName = "Ready room",
  sessionId,
  groupCode,
  userId,
  hasClickedBegin,
  participantCount,
  role,
  authMethod,
  // One-shot stuck detectors. When set, onWarn/onFail fire once if the user is
  // still on this screen after the given ms. Used by the "OKO preparing" room —
  // where any prolonged stay means the session failed to initialize — but NOT
  // by the ready room, where students legitimately wait minutes for teammates.
  warnThresholdMs = null,
  failThresholdMs = null,
  onWarn,
  onFail,
  // Optional caller-owned entry stamp. A connection blip swaps the Join screen
  // in and remounts the room component, which would otherwise restart the
  // stuck detectors from zero on every blip; a ref that outlives the component
  // keeps the clock running across remounts.
  enteredAtRef: externalEnteredAtRef,
}) => {
  const [totalWaitTime, setTotalWaitTime] = useState(0);

  // Use refs to always have the latest values in the interval callback
  const hasClickedBeginRef = useRef(hasClickedBegin);
  const participantCountRef = useRef(participantCount);

  // Wall-clock entry timestamp. Unlike totalWaitTime — which advances in flat
  // 60s steps and undercounts when setInterval is throttled in a backgrounded
  // tab — this yields the true elapsed time a user spent on the screen, which
  // is what "how long were they stuck" actually needs.
  const localEnteredAtRef = useRef(null);
  const enteredAtRef = externalEnteredAtRef || localEnteredAtRef;

  // Reason the room is being left. Components call reportExit() to set this
  // right before they trigger a transition; defaults to "unmount" for the
  // plain teardown case (tab closed, navigated away).
  const exitReasonRef = useRef("unmount");

  // Callbacks live in refs so a fresh arrow function from the caller doesn't
  // re-run the mount effect and re-arm (i.e. reset) the threshold timers.
  const onWarnRef = useRef(onWarn);
  const onFailRef = useRef(onFail);
  onWarnRef.current = onWarn;
  onFailRef.current = onFail;

  // Keep refs updated with latest props
  useEffect(() => {
    hasClickedBeginRef.current = hasClickedBegin;
  }, [hasClickedBegin]);

  useEffect(() => {
    participantCountRef.current = participantCount;
  }, [participantCount]);

  useEffect(() => {
    // A caller-owned stamp is only set on genuine entry, so a remount or a dep
    // change (sessionId churn on reconnect) does not restart the clock. The
    // local stamp keeps its original per-effect-run semantics.
    if (!externalEnteredAtRef || enteredAtRef.current == null) enteredAtRef.current = Date.now();

    // Log immediately when entering room
    logSession(`${roomName} entered`, {
      roomName,
      sessionId,
      groupCode,
      userId,
      totalWaitTime: 0,
      durationMs: 0,
      hasClickedBegin: hasClickedBeginRef.current,
      participantCount: participantCountRef.current,
      role,
      authMethod,
    });

    const intervalId = setInterval(() => {
      setTotalWaitTime((prevTime) => {
        const newTime = prevTime + 60;

        logSession(`${roomName} heartbeat`, {
          roomName,
          sessionId,
          groupCode,
          userId,
          totalWaitTime: newTime,
          durationMs: Date.now() - enteredAtRef.current,
          hasClickedBegin: hasClickedBeginRef.current,
          participantCount: participantCountRef.current,
          role,
          authMethod,
        });

        return newTime;
      });
    }, HEARTBEAT_INTERVAL_MS);

    // One-shot stuck detectors, armed with the time *remaining* so a re-arm
    // (remount, dep change) picks up where the clock left off. Cleared on
    // unmount, which is also what disarms them on success (the room transition
    // swaps the component out). Elapsed is re-checked from the wall clock inside
    // each callback because setTimeout in a backgrounded tab is throttled and
    // can't be trusted on its own.
    const armThreshold = (thresholdMs, callbackRef) =>
      thresholdMs != null && callbackRef.current
        ? setTimeout(() => {
            if (Date.now() - enteredAtRef.current < thresholdMs) return;
            callbackRef.current();
          }, Math.max(0, thresholdMs - (Date.now() - enteredAtRef.current)))
        : null;
    const warnTimer = armThreshold(warnThresholdMs, onWarnRef);
    const failTimer = armThreshold(failThresholdMs, onFailRef);

    // Cleanup interval on unmount, and close out the room lifecycle with an
    // exit event so dwell time can be measured precisely (vs. inferring it
    // from the absence of further heartbeats) and stays under 60s are captured.
    return () => {
      clearInterval(intervalId);
      if (warnTimer) clearTimeout(warnTimer);
      if (failTimer) clearTimeout(failTimer);

      logSession(`${roomName} exited`, {
        roomName,
        sessionId,
        groupCode,
        userId,
        durationMs: enteredAtRef.current ? Date.now() - enteredAtRef.current : 0,
        exitReason: exitReasonRef.current,
        hasClickedBegin: hasClickedBeginRef.current,
        participantCount: participantCountRef.current,
        role,
        authMethod,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName, sessionId, groupCode, userId, role, authMethod, warnThresholdMs, failThresholdMs]);

  const resetWaitTime = () => {
    setTotalWaitTime(0);
  };

  // Set the reason logged with the next `${roomName} exited` event.
  const reportExit = (reason) => {
    exitReasonRef.current = reason;
  };

  // True wall-clock ms since the room was entered. Used by callers that need
  // to stamp a transition event (e.g. "Session activity started") with the
  // exact time spent on the screen.
  const getElapsedMs = () =>
    enteredAtRef.current ? Date.now() - enteredAtRef.current : 0;

  return {
    totalWaitTime,
    resetWaitTime,
    reportExit,
    getElapsedMs,
  };
};
